import { useEffect, useState } from 'react';
import { usePrevious } from 'react-use';

import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Select,
  SelectComponents,
} from '@frontend/components';

interface CellControlCornerRadii {
  isFirstItemInTable?: boolean;
  isLastItemInTable?: boolean;
}

const createCellControl = ({
  isFirstItemInTable,
  isLastItemInTable,
}: CellControlCornerRadii) => {
  const CellControlWithRadii = (
    props: React.ComponentProps<typeof SelectComponents.Control>,
  ) => (
    <SelectComponents.Control
      {...props}
      innerProps={{
        ...props.innerProps,
        border: 'none',
        height: '100%',
        ...(isFirstItemInTable ? { borderTopRightRadius: 'lg' } : {}),
        ...(isLastItemInTable ? { borderBottomRightRadius: 'lg' } : {}),
        _focus: { borderColor: 'transparent', boxShadow: 'focusInset' },
      }}
    />
  );

  CellControlWithRadii.displayName = 'CellControlWithRadii';
  return CellControlWithRadii;
};

const CellValueContainer = (
  props: React.ComponentProps<typeof SelectComponents.ValueContainer>,
) => (
  <SelectComponents.ValueContainer
    {...props}
    innerProps={{
      ...props.innerProps,
      px: 0.5,
    }}
  />
);

const CellSelectContainer = (
  props: React.ComponentProps<typeof SelectComponents.SelectContainer>,
) => (
  <SelectComponents.SelectContainer
    {...props}
    innerProps={{
      ...props.innerProps,
      height: '100%',
    }}
  />
);

interface StatusOption {
  label: JSX.Element;
  value: string;
}

interface StatusOptionGroup {
  options: StatusOption[];
}

interface StatusSelectorProps {
  isReadOnly: boolean;
  label: string;
  onChange: (selectedStatus: string) => void;
  currentStatus: string;
  statusOptions: StatusOptionGroup[];
  isSelectorInTable?: boolean;
  isFirstItemInTable?: boolean;
  isLastItemInTable?: boolean;
}

const StatusSelector = ({
  isReadOnly,
  label,
  onChange,
  currentStatus,
  statusOptions,
  isSelectorInTable = false,
  isFirstItemInTable = false,
  isLastItemInTable = false,
}: StatusSelectorProps) => {
  const prevStatus = usePrevious(currentStatus);

  const [status, setStatus] = useState(currentStatus);
  const selectedOption = statusOptions
    ?.flatMap((optionGroup) => optionGroup.options)
    .find((option) => option?.value === status);

  useEffect(() => {
    if (currentStatus !== prevStatus) {
      setStatus(currentStatus);
    }
  }, [currentStatus, prevStatus]);

  const select = (
    <Select
      value={selectedOption}
      options={statusOptions}
      size="md"
      fontSize="md"
      isDisabled={isReadOnly}
      isClearable={false}
      blurInputOnSelect
      components={{
        ValueContainer: CellValueContainer,
        ...(isSelectorInTable
          ? {
              Control: createCellControl({
                isFirstItemInTable,
                isLastItemInTable,
              }),
              SelectContainer: CellSelectContainer,
            }
          : {}),
      }}
      styles={{ input: () => ({ paddingLeft: '6px' }) }}
      onChange={({ value }: { value: string }) => {
        setStatus(value);
        onChange(value);
      }}
    />
  );

  if (isSelectorInTable) {
    return select;
  }

  return (
    <Box py={0.75}>
      <FormControl>
        <HStack>
          <Box w={16} marginRight={2}>
            <FormLabel height="auto" marginBottom={0}>
              {label}
            </FormLabel>
          </Box>
          <Flex minWidth={34} flexGrow={1}>
            {select}
          </Flex>
        </HStack>
      </FormControl>
    </Box>
  );
};

export default StatusSelector;
